import { createContext, useContext, useEffect, useMemo } from "react";
import { useLocation } from "wouter";
import { DEFAULT_BRAND, resolveBrandConfig, type BrandConfig } from "@/branding/brandConfig";
import { useAuth } from "@/contexts/AuthContext";
import { isAuthRoutePath, stripQueryAndHash } from "@/lib/authRedirect";

interface BrandContextValue {
  brand: BrandConfig;
  homePath: string;
  isSjtuSoe: boolean;
}

const BrandContext = createContext<BrandContextValue | undefined>(undefined);

export function BrandProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [location] = useLocation();
  const routePath = stripQueryAndHash(location);
  const usesNeutralBrand = routePath.startsWith("/share/") || isAuthRoutePath(routePath);
  const brand = useMemo(
    () => usesNeutralBrand ? DEFAULT_BRAND : resolveBrandConfig(user?.organization?.code),
    [usesNeutralBrand, user?.organization?.code],
  );

  useEffect(() => {
    const root = document.documentElement;
    if (brand.key === DEFAULT_BRAND.key) {
      delete root.dataset.brand;
    } else {
      root.dataset.brand = brand.key;
    }
    document.title = brand.browserTitle;
  }, [brand]);

  const value = useMemo(
    () => ({ brand, homePath: brand.homePath, isSjtuSoe: brand.key === "sjtu-soe" }),
    [brand],
  );

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

export function useBrand() {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error("useBrand must be used within BrandProvider");
  }
  return context;
}
